/**
 * light-tokens.ts — the ONLY place a light-band hex/serif-var/shadow resolves
 * (R4-P3 masterplan §1.2 "LIGHT-BAND TOKEN SET" + §1.3 "the `ui-light/**`
 * layer", as amended by [AMENDED per C1/C2/C3]). Mirrors `ui/tone.ts`'s
 * discipline for the dark system: every consumer imports the pre-baked
 * Tailwind-class STRING from here — never hand-composes `text-[${hex}]` at a
 * call site — so there is exactly one place a light-band color can drift.
 *
 * Contrast-corrected per [AMENDED per C1] (WCAG, independently re-verified
 * against `#F6F5F2`, this session): the masterplan's original §1.2 draft used
 * `#8A8F98` (2.98:1, FAILS AA) for the two-tone continuation/body role and
 * `#9CA3B0` (2.33:1, FAILS AA) for the eyebrow/attribution role. Both are
 * corrected here:
 *   - `ink.secondary` (two-tone continuation + body text) → `#6B707A` (4.56:1 ✓)
 *   - `ink.tertiary` (stat labels, attribution name) → `#5C616B` (5.70:1 ✓, unchanged)
 *   - `ink.eyebrowDecorative` (`#9CA3B0`, 2.33:1 ✗) is kept ONLY for
 *     decorative, non-content use (dot fills, hairline accents) — every
 *     consumer MUST check `contentSafe` before using it to render text.
 *
 * `check-landing-band-hue.mjs` allowlists exactly these hexes for
 * `src/features/marketing/ui-light/**` (§4 gate delta) — any OTHER hex
 * (amber, chapter tone, or the dark neutral ramp) on a light-band file FAILS
 * closed (Law #4: light bands are ink-only, no amber, no chapter tones).
 *
 * R7 HARMONIZATION NOTE (2026-07-17, hallmark paper-flip round, L4
 * "harmonization only" scope — read this file, do not edit its exported
 * values from this lane): `ui/paper-tokens.ts` is a NEW, SEPARATE registry
 * for the same round's page-chrome paper flip, and its hexes are close-but-
 * NOT-identical to this file's (`#F7F1E9` vs this file's `#F6F5F2` paper;
 * `#1C1712` vs `#15171C` ink primary; `#47413C` vs `#6B707A` ink secondary).
 * This is NOT drift to reconcile in this pass — this file has real
 * consumers beyond the landing page's dark chrome (`SocialProofSection`,
 * `SerifQuote`, `BigStatCurve`, `LightEyebrow`, `LightBand`, the dev gallery
 * specimens; verified via `gitnexus`/grep before this note was added), so a
 * lane scoped to page-chrome components cannot safely repoint or merge this
 * registry's values without touching every one of those files. Flagged for
 * integration: if a future round wants ONE unified light-neutral system
 * instead of two adjacent-but-distinct ones, that is a cross-cutting change
 * with its own blast radius, not a byproduct of this round's page-chrome
 * flip.
 */

/** The 4 ink roles used across every light-band surface. */
export type LightInkRole = 'primary' | 'secondary' | 'tertiary' | 'eyebrowDecorative';

export type LightInkRecipe = {
  /** Raw hex — inline style / raw SVG `stroke`/`fill` attribute consumers
   * (`BigStatCurve`, the big-stat left-rule) that cannot pre-bake a static
   * Tailwind class. */
  hex: string;
  /** Pre-baked `text-[#hex]` Tailwind class. */
  text: string;
  /** WCAG contrast ratio on `#F6F5F2`, independently re-verified
   * [AMENDED per C1] — documentation only, not read at runtime. */
  contrastOnPaper: number;
  /** [AMENDED per C1]: `false` means this ink FAILS the 4.5:1 AA floor on
   * paper and must never carry readable text content — decorative fills/
   * dots/borders only. `measure-gallery.mjs`'s P4 pass asserts ≥4.5:1 per
   * text node under `.ink-scope`; this flag is the compile-time reminder. */
  contentSafe: boolean;
};

export const LIGHT_TOKENS = {
  paper: { hex: '#F6F5F2', bg: 'bg-[#F6F5F2]' },

  ink: {
    primary: {
      hex: '#15171C',
      text: 'text-[#15171C]',
      contrastOnPaper: 16.45,
      contentSafe: true,
    },
    secondary: {
      hex: '#6B707A',
      text: 'text-[#6B707A]',
      contrastOnPaper: 4.56,
      contentSafe: true,
    },
    tertiary: {
      hex: '#5C616B',
      text: 'text-[#5C616B]',
      contrastOnPaper: 5.7,
      contentSafe: true,
    },
    eyebrowDecorative: {
      hex: '#9CA3B0',
      text: 'text-[#9CA3B0]',
      contrastOnPaper: 2.33,
      contentSafe: false,
    },
  } satisfies Record<LightInkRole, LightInkRecipe>,

  card: {
    fillHex: '#FFFFFF',
    fillBg: 'bg-[#FFFFFF]',
    /** Diabrowser-measured. Doubles as the light-seam hairline (see `seam`
     * below) and the in-card fainter divider is `borderFaint`. */
    border: 'border-black/[0.08]',
    borderFaint: 'border-black/[0.06]',
    /** Floating cards only — flat light cards are border-only, no shadow. */
    shadow: '0 2px 8px rgba(0,0,0,.06), 0 0 2px rgba(0,0,0,.04)',
  },

  /** The neutralized pill eyebrow recipe (masterplan §1.2) — attio's pill
   * eyebrow with the blue tint removed (Law #4: light bands are ink-only). */
  /** R6 mono voice law (judge §2.3): converted from font-mono to Inter +
   * tabular-nums -- the page's ONLY mono slot is the Product-OS chapter
   * numerals (a different file's concern, not this one). ZERO color change
   * (typography-only; Law #4 "light bands are ink-only" is untouched). */
  eyebrowChip:
    'inline-flex items-center gap-1.5 rounded-full border border-black/[0.08] bg-black/[0.04] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] tabular-nums text-[#5C616B]',

  /** Big-stat left-rule: 2px wide, ink, ~60% of the stat block's height. */
  statRuleBg: 'bg-[#15171C]',

  /** `BigStatCurve`'s ink sweep — [AMENDED per B2]. */
  curve: {
    stroke: '#15171C',
    strokeOpacity: 0.55,
    areaFill: 'rgba(0,0,0,0.035)',
  },

  /** [AMENDED per B4]: the dark↔light `NarrativeHandoff` seam is leaderless/
   * chipless — ONE neutral 1px midpoint hairline, reusing the same
   * `rgba(0,0,0,0.08)` value as the light card border/hairline above (it
   * "doubles as the divider/hairline on light" per §1.2). */
  seam: {
    hairline: 'rgba(0,0,0,0.08)',
  },

  /** [AMENDED per C4]: LOCKED 0-byte system-serif stack, `font-weight:400` +
   * `font-synthesis:none` (system Palatino/Book Antiqua expose regular/bold
   * only — a requested 500 gets inconsistently browser-synthesized). The var
   * itself is declared once in `globals.css`; this is the name every
   * consumer reads via `var(--font-landing-serif)`. */
  serifFontVar: 'var(--font-landing-serif)',

  /** [AMENDED per C3]: the baked dot-grid asset — `scripts/generate-light-
   * paper-dots.mjs` is the deterministic generator; `size` must match the
   * `background-size` the `.light-paper-dots` globals.css class declares. */
  dotGrid: {
    src: '/light-paper-dots.png',
    size: 96,
  },
} as const;

export type LightTokens = typeof LIGHT_TOKENS;
